import type { Color } from "common/utilities/colors/colors.types";
import type {
  SequenceAlign,
  SequenceFeature,
  SequenceFeatureTypeColorSetting,
} from "sequenceAnalyser/api/sequenceAnalyserApiClient";

export enum SequenceAnalyserTableViews {
  default = "default",
  abbreviated = "abbreviated",
  abbreviatedWithoutColors = "abbreviatedWithoutColors",
  abbreviatedWithoutColorsAndFeaturesColors = "abbreviatedWithoutColorsAndFeaturesColors",
}

export type SequenceAnalyserTableView = keyof typeof SequenceAnalyserTableViews;

export enum SequenceAnalyserVisualisations {
  consensus = "consensus",
  conservation = "conservation",
  logo = "logo",
}

export type SequenceAnalyserVisualisation = keyof typeof SequenceAnalyserVisualisations;

// A feature that keeps a reference to the sequence its from
export interface SequenceFeatureWithRef extends SequenceFeature {
  sequenceName: string;
}

export interface FeatureDefinition {
  name: string;
  /** ID of the feature type */
  typeId: number;
  uniqueTypeString: FeatureId;
  /** Label of the feature type */
  type: string;
  color: Color;
  source: ColorSource;
  visible: boolean;
  features: SequenceFeatureWithRef[];
}

/** featureType_featureName */
export type FeatureId = `${string}_${string}`;

/**
 * FeatureDefinitions groups sequences features with the same name together
 * and add specific attributes related to the interface (color, visilibity etc.)
 */
export type FeatureDefinitions = Record<FeatureId, FeatureDefinition>;

export enum ColorSources {
  hardcoded = "hardcoded",
  global = "global",
  user = "user",
  forced = "forced",
}

type ColorSource = keyof typeof ColorSources;

export type FeaturesColors = Record<FeatureId, { color: Color; source: ColorSource }>;

export type FeatureTypesColor = Record<string, { color: Color; source: ColorSource }>;

/** Contains settings of a given feature type */
export interface SequenceFeaturesType {
  uniqueTypeString: FeatureId;
  /** ID of the feature type */
  typeId: number;
  /** Label of the feature type */
  type: string;
  definitions: FeatureDefinition[];
  color: Color;
  colorSetting: SequenceFeatureTypeColorSetting;
  colorSource: ColorSource;
}

export interface Residue {
  letter: string;
  sequenceQualifiedId: string;
  sequenceName: string;
  /** Position in the visible sequence (aligned or not) */
  position: number;
  /** Unaligned position (sequence position): undefined means it's a gap */
  unAlignedPosition?: number;
  featureDefinitions: FeatureDefinition[];
  // Position on the screen
  top: number;
  left: number;
}

export interface RemovedSequence {
  position: number;
  sequence: SequenceAlign;
}

export type VisibleSequencesFeatures = Record<string, SequenceFeature[]>;
